import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../stores';

interface Activity {
  id: string;
  type: 'user' | 'project' | 'system' | 'export' | 'security';
  action: string;
  user: string;
  details: string;
  timestamp: string;
  ipAddress?: string;
}

interface SignalRConnectionRow {
  connectionId: string;
  userId: string;
  email: string;
  connectedAtUtc: string;
}

const activityTypes: Activity['type'][] = ['user', 'project', 'system', 'export', 'security'];

function normalizeActivityType(type: string): Activity['type'] {
  const t = type.toLowerCase();
  return activityTypes.includes(t as Activity['type']) ? (t as Activity['type']) : 'system';
}

export function ActivityLog() {
  const { accessToken } = useAuthStore();
  const [signalrConnections, setSignalrConnections] = useState<SignalRConnectionRow[]>([]);
  const [signalrMeta, setSignalrMeta] = useState({ totalConnections: 0, uniqueUsers: 0 });
  const [signalrError, setSignalrError] = useState('');
  const [signalrLoading, setSignalrLoading] = useState(true);

  const fetchSignalRConnections = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/signalr-connections', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        throw new Error('SignalR bağlantı listesi alınamadı');
      }
      const data = (await response.json()) as {
        totalConnections: number;
        uniqueUsers: number;
        connections: SignalRConnectionRow[];
      };
      setSignalrMeta({
        totalConnections: data.totalConnections,
        uniqueUsers: data.uniqueUsers,
      });
      setSignalrConnections(data.connections ?? []);
      setSignalrError('');
    } catch (e) {
      setSignalrError(e instanceof Error ? e.message : 'Yükleme hatası');
    } finally {
      setSignalrLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    setSignalrLoading(true);
    void fetchSignalRConnections();
    const id = window.setInterval(() => {
      void fetchSignalRConnections();
    }, 4000);
    return () => window.clearInterval(id);
  }, [fetchSignalRConnections]);

  const [activities, setActivities] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState('');

  const fetchActivityLogs = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/activity-logs', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        throw new Error('Etkinlik günlüğü yüklenemedi');
      }
      const data = (await response.json()) as Array<{
        id: string;
        type: string;
        action: string;
        user: string;
        details: string;
        timestamp: string;
        ipAddress?: string | null;
      }>;
      setActivities(
        data.map((row) => ({
          id: row.id,
          type: normalizeActivityType(row.type),
          action: row.action,
          user: row.user,
          details: row.details,
          timestamp: row.timestamp,
          ipAddress: row.ipAddress ?? undefined,
        })),
      );
      setActivitiesError('');
    } catch (e) {
      setActivitiesError(e instanceof Error ? e.message : 'Yükleme hatası');
    } finally {
      setActivitiesLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    setActivitiesLoading(true);
    void fetchActivityLogs();
  }, [fetchActivityLogs]);

  const [filterType, setFilterType] = useState<'all' | Activity['type']>('all');  const [searchQuery, setSearchQuery] = useState('');

  const filteredActivities = activities.filter((activity) => {
    const matchesType = filterType === 'all' || activity.type === filterType;
    const matchesSearch =
      activity.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.details.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'user':
        return '👤';
      case 'project':
        return '📁';
      case 'system':
        return '⚙️';
      case 'export':
        return '📤';
      case 'security':
        return '🔒';
      default:
        return '📝';
    }
  };

  const getActivityTypeClass = (type: Activity['type']) => {
    return `activity-type-badge activity-type-${type}`;
  };

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Activity</h1>
        <p>SignalR (movieHub) canlı bağlantıları ve veritabanındaki etkinlik günlüğü</p>
      </header>

      <section className="admin-table-section admin-section-spaced">
        <div className="admin-table-toolbar">
          <h2>SignalR — bağlı istemciler</h2>
          <span className="admin-signalr-live">
            <span className="admin-signalr-live-dot" aria-hidden />
            Canlı
          </span>
        </div>
        {signalrError && <div className="admin-signalr-error">{signalrError}</div>}
        <div className="admin-signalr-meta">
          <span>
            <strong>{signalrMeta.totalConnections}</strong> açık bağlantı
          </span>
          <span>
            <strong>{signalrMeta.uniqueUsers}</strong> benzersiz kullanıcı
          </span>
          {signalrLoading && <span>Listeleniyor…</span>}
        </div>
        {signalrConnections.length === 0 && !signalrLoading ? (
          <div className="admin-signalr-empty">Şu anda movieHub’a bağlı aktif istemci yok.</div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>E-posta</th>
                  <th>Kullanıcı ID</th>
                  <th>Bağlantı ID</th>
                  <th>Bağlandı (UTC)</th>
                </tr>
              </thead>
              <tbody>
                {signalrConnections.map((row) => (
                  <tr key={row.connectionId}>
                    <td>{row.email || '—'}</td>
                    <td>
                      <code className="admin-code-highlight">{row.userId}</code>
                    </td>
                    <td>
                      <code className="admin-code-subtle">{row.connectionId}</code>
                    </td>
                    <td>{new Date(row.connectedAtUtc).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="admin-table-section">
        <div className="admin-table-toolbar">
          <h2>Etkinlik günlüğü</h2>
          <div className="admin-card-actions">
            <input
              type="text"
              placeholder="Search activities..."
              className="admin-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="admin-filter-buttons">
              <button
                className={filterType === 'all' ? 'admin-filter-btn active' : 'admin-filter-btn'}
                onClick={() => setFilterType('all')}
              >
                All
              </button>
              <button
                className={filterType === 'user' ? 'admin-filter-btn active' : 'admin-filter-btn'}
                onClick={() => setFilterType('user')}
              >
                Users
              </button>
              <button
                className={
                  filterType === 'project' ? 'admin-filter-btn active' : 'admin-filter-btn'
                }
                onClick={() => setFilterType('project')}
              >
                Projects
              </button>
              <button
                className={
                  filterType === 'export' ? 'admin-filter-btn active' : 'admin-filter-btn'
                }
                onClick={() => setFilterType('export')}
              >
                Exports
              </button>
              <button
                className={
                  filterType === 'security' ? 'admin-filter-btn active' : 'admin-filter-btn'
                }
                onClick={() => setFilterType('security')}
              >
                Security
              </button>
              <button
                className={
                  filterType === 'system' ? 'admin-filter-btn active' : 'admin-filter-btn'
                }
                onClick={() => setFilterType('system')}
              >
                System
              </button>
            </div>
          </div>
        </div>

        {activitiesError && <div className="admin-signalr-error">{activitiesError}</div>}
        {activitiesLoading && <p className="admin-loading-text">Etkinlikler yükleniyor…</p>}
        {!activitiesLoading && !activitiesError && filteredActivities.length === 0 && (
          <div className="admin-signalr-empty">Henüz kayıt yok. Veritabanında admin_activity_logs tablosu oluştu mu?</div>
        )}
        <div className="admin-activity-list">
          {filteredActivities.map((activity) => (
            <div key={activity.id} className="admin-activity-item">
              <div className="admin-activity-icon">{getActivityIcon(activity.type)}</div>
              <div className="admin-activity-content">
                <div className="admin-activity-header">
                  <span className="admin-activity-action">{activity.action}</span>
                  <span className={getActivityTypeClass(activity.type)}>{activity.type}</span>
                </div>
                <div className="admin-activity-details">{activity.details}</div>
                <div className="admin-activity-meta">
                  <span className="admin-activity-user">👤 {activity.user}</span>
                  <span className="admin-activity-time">
                    🕐 {new Date(activity.timestamp).toLocaleString()}
                  </span>
                  {activity.ipAddress && (
                    <span className="admin-activity-ip">🌐 {activity.ipAddress}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}
